// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Alert, AppIcon, Modal, Spinner } from '@loa/ui-components';
import { chartPalette } from '@loa/design-tokens';
import { croApi, type CroCompanyHolding } from '../../api/croApi';
import { CroCompanyList } from '../cro/CroCompanyList';
import {
  financialPositionApi,
  type AccountSummary,
  type DashAccountRow,
  type DashDonutSlice,
  type DashIncomeRow,
  type DashPolicyValue,
  type DashPropertyRow,
  type FigureVersion,
  type FinancialDashboard as Dashboard,
  type FinancialProjection,
} from '../../api/financialPositionApi';

interface Props {
  profileId: string;
  /** External refresh signal — bump to refetch (shared with sibling cards). */
  refreshSignal?: number;
  /** Called after an income change so the parent can refresh sibling cards too. */
  onChanged?: () => void;
}

const PALETTE = chartPalette;

function money(amount: number | null | undefined, currency: string | null): string {
  if (amount === null || amount === undefined) return '—';
  const code = currency ?? 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount).toLocaleString()} ${code}`;
  }
}

/** Disabled edit affordance — real editing is a future phase (PRD §9). */
function EditButton() {
  return (
    <button
      type="button"
      disabled
      aria-label="Editing coming soon"
      title="Editing coming soon"
      data-testid="fp-card-edit"
      className="flex items-center justify-center w-12 h-12 rounded-button border-[1.5px] border-[#BDB8FF] bg-white cursor-not-allowed opacity-60"
    >
      <AppIcon name="edit" size={20} color="#2D0A6C" />
    </button>
  );
}

/** Card chrome matching the relationship Overview design. */
/**
 * Confirmed corporate holdings for the relationship, aggregated across its
 * clients (cro-data-service via the /api/cro relay). Matches are seeded during
 * relationship creation for clients flagged as a business owner / company
 * shareholder. Renders the empty prompt until any are confirmed, and degrades
 * to the same prompt if the CRO integration is disabled (endpoint 404s).
 */
function CorporateHoldingsPanel({ profileId }: { profileId: string }) {
  const [companies, setCompanies] = useState<CroCompanyHolding[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    croApi.getProfileHoldings(profileId)
      .then((res) => { if (!cancelled) setCompanies(res.companies); })
      .catch(() => { if (!cancelled) setCompanies([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [profileId]);

  if (loading) return <div className="flex justify-center py-10"><Spinner /></div>;
  if (!companies || companies.length === 0) {
    return <EmptyPanel message="No corporate holdings confirmed yet for this relationship." />;
  }
  return <CroCompanyList companies={companies} />;
}

function ChartCard({ title, wide, children }: { title: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={`overview-chart-placeholder p-md h-full flex flex-col ${wide ? 'overview-chart-placeholder--wide' : ''}`}>
      <div className="flex items-center justify-between pb-lg">
        <div className="text-black text-body-01 font-medium leading-normal font-sans">{title}</div>
        <EditButton />
      </div>
      {children}
    </div>
  );
}

function EmptyPanel({ message }: { message: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-[10px] self-stretch rounded-card bg-lilac-06 flex-1 py-10">
      <AppIcon name="chart" size={24} />
      <div className="text-center text-body-02 text-black font-normal leading-normal">{message}</div>
    </div>
  );
}

/**
 * Advisor relationship Overview financial dashboard — renders the projected
 * record layer as cards in the existing Overview chrome (clients as rows; Joint
 * its own row/bar; annualised; edit disabled). See
 * docs/specs/financial-position-dashboard-prd.md.
 */
export function FinancialDashboard({ profileId, refreshSignal, onChanged }: Props) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // When a parent drives refresh (to also refetch sibling cards), delegate to it;
  // otherwise refetch ourselves.
  const reload = onChanged ?? (() => setReloadKey((k) => k + 1));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    financialPositionApi
      .getDashboard(profileId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the financial dashboard.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId, reloadKey, refreshSignal]);

  if (loading) return <Spinner />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!data) return null;

  const c = data.currency;

  return (
    <div data-testid="financial-dashboard">
      <div className="overview-charts-row">
        <ChartCard title="Pensions">
          <ComponentBars policies={data.pensions.policies} currency={c} layout="vertical" empty="Add policies to start receiving data analytics" />
          <PolicyTriggers policies={data.pensions.policies} kind="pensions" currency={c} profileId={profileId} />
        </ChartCard>
        <ChartCard title="Investments">
          <ComponentBars policies={data.investments.policies} currency={c} layout="horizontal" empty="Add policies to start receiving data analytics" />
          <PolicyTriggers policies={data.investments.policies} kind="investments" currency={c} profileId={profileId} />
        </ChartCard>
      </div>

      <div className="overview-charts-row overview-charts-row--3col">
        <ChartCard title="Protection overview">
          {data.protection.total > 0 ? (
            <Donut
              slices={protectionSlices(data)}
              centerLabel="Total worth"
              centerValue={money(data.protection.total, c)}
              currency={c}
            />
          ) : (
            <EmptyPanel message="Add policies to start receiving data analytics" />
          )}
        </ChartCard>
        <ChartCard title="Corporate Holdings" wide>
          <CorporateHoldingsPanel profileId={profileId} />
        </ChartCard>
      </div>

      <div className="overview-charts-row">
        <ChartCard title="Expenditure">
          {data.expenditure.totalAnnual > 0 ? (
            <>
              <RowsTable
                rows={data.expenditure.rows.map((r) => ({ label: r.label, owner: '', amount: r.annual }))}
                currency={c}
                negative
              />
              <Donut slices={data.expenditure.donut} centerLabel="Total Expenditure" centerValue={money(data.expenditure.totalAnnual, c)} currency={c} />
            </>
          ) : (
            <EmptyPanel message="Add expenditure details to start receiving data analytics" />
          )}
        </ChartCard>
        <ChartCard title="Income">
          {data.income.rows.length > 0 ? (
            <>
              <IncomeTable rows={data.income.rows} currency={c} profileId={profileId} onReconciled={reload} />
              <IncomeCharts
                rows={data.income.rows}
                donut={data.income.donut}
                totalAnnual={data.income.totalAnnual}
                currency={c}
              />
            </>
          ) : (
            <EmptyPanel message="Add income details to start receiving data analytics" />
          )}
        </ChartCard>
      </div>

      <div className="flex flex-col w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] pt-lg pr-md pb-lg pl-md gap-lg mb-lg">
        <div className="flex items-center justify-between">
          <div className="text-black text-body-01 font-medium leading-normal font-sans">Account Balances</div>
          <EditButton />
        </div>
        {data.accountBalances.length > 0 ? (
          <AccountBalancesTable rows={data.accountBalances} currency={c} profileId={profileId} />
        ) : (
          <EmptyPanel message="Add account details to start receiving data analytics" />
        )}
      </div>

      <div className="flex flex-col w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] pt-lg pr-md pb-lg pl-md gap-lg mb-lg">
        <div className="flex items-center justify-between">
          <div className="text-black text-body-01 font-medium leading-normal font-sans">Property</div>
          <EditButton />
        </div>
        {data.property.rows.length > 0 ? (
          <PropertyTable property={data.property} currency={c} profileId={profileId} />
        ) : (
          <EmptyPanel message="Add property details to start receiving data analytics" />
        )}
      </div>
    </div>
  );
}

function protectionSlices(data: Dashboard): DashDonutSlice[] {
  return [
    { label: 'Life cover', amount: data.protection.lifeCover },
    { label: 'Income protection', amount: data.protection.incomeProtection },
    { label: 'Illness benefit', amount: data.protection.illnessBenefit },
  ].filter((s) => s.amount > 0);
}

/**
 * Stacked bar per owner, broken down into the owner's component policies/holdings
 * (one coloured segment per component). Legend lists the components.
 */
function ComponentBars({
  policies,
  currency,
  layout,
  empty,
}: {
  policies: DashPolicyValue[];
  currency: string | null;
  layout: 'vertical' | 'horizontal';
  empty: string;
}) {
  if (policies.length === 0) return <EmptyPanel message={empty} />;
  // Distinct component labels (stack keys) + one row per owner with each component's value.
  const labels: string[] = [];
  const ownerOrder: string[] = [];
  const rowByOwner = new Map<string, Record<string, number | string>>();
  for (const p of policies) {
    if (!labels.includes(p.label)) labels.push(p.label);
    if (!rowByOwner.has(p.owner)) {
      rowByOwner.set(p.owner, { owner: p.owner });
      ownerOrder.push(p.owner);
    }
    const row = rowByOwner.get(p.owner)!;
    row[p.label] = Number(row[p.label] ?? 0) + p.value;
  }
  const data = ownerOrder.map((o) => {
    const row = rowByOwner.get(o)!;
    labels.forEach((l) => {
      if (row[l] === undefined) row[l] = 0;
    });
    return row;
  });
  const bars = labels.map((l, i) => (
    <Bar key={l} dataKey={l} stackId="a" fill={PALETTE[i % PALETTE.length]} />
  ));
  return (
    <div className="flex flex-col flex-1 gap-[12px]">
      <div className="flex flex-wrap gap-md" data-testid="fp-component-legend">
        {labels.map((l, i) => (
          <div key={l} className="flex items-center gap-sm">
            <span style={{ width: 16, height: 16, borderRadius: 5, background: PALETTE[i % PALETTE.length] }} />
            <span className="text-body-03 text-black">{l}</span>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        {layout === 'horizontal' ? (
          <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16, top: 8, bottom: 8 }}>
            <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => money(v, currency)} />
            <YAxis type="category" dataKey="owner" tick={{ fontSize: 12 }} width={72} />
            <Tooltip formatter={(value) => money(Number(value), currency)} />
            {bars}
          </BarChart>
        ) : (
          <BarChart data={data} margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
            <XAxis dataKey="owner" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => money(v, currency)} />
            <Tooltip formatter={(value) => money(Number(value), currency)} />
            {bars}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/** Donut hover-tooltip formatter: `[€amount, sliceLabel]`. Exported for unit test. */
export function donutTooltipFormatter(currency: string | null) {
  return (value: unknown, name: unknown): [string, string] => [
    money(Number(value), currency),
    String(name),
  ];
}

function Donut({
  slices,
  centerLabel,
  centerValue,
  hideLegend,
  currency,
}: {
  slices: DashDonutSlice[];
  centerLabel: string;
  centerValue: string;
  hideLegend?: boolean;
  currency?: string | null;
}) {
  return (
    <div className="flex flex-col items-center gap-[12px] w-full">
      <div style={{ position: 'relative', width: '100%', height: 220 }}>
        <ResponsiveContainer width="100%" height={220} minWidth={0} minHeight={220}>
          <PieChart>
            <Pie data={slices} dataKey="amount" nameKey="label" innerRadius={60} outerRadius={90} paddingAngle={2}>
              {slices.map((_, i) => (
                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </Pie>
            <Tooltip formatter={donutTooltipFormatter(currency ?? null)} />
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            textAlign: 'center',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        >
          <div className="text-body-03 text-grey-04">{centerLabel}</div>
          <div className="text-body-01 font-medium text-black font-sans">{centerValue}</div>
        </div>
      </div>
      {!hideLegend && <DonutLegend slices={slices} />}
    </div>
  );
}

/**
 * Income donuts: a single gross donut when the tax layer is off, else two
 * side-by-side pies — Before tax (gross per source) and After tax (net per
 * source) — sharing one legend (same source order ⇒ same colour in both). The
 * net per source uses the client's PROVIDED net when reconciled (it wins over our
 * estimate); otherwise gross − the calculated tax.
 */
function IncomeCharts({ rows, donut, totalAnnual, currency }: {
  rows: DashIncomeRow[];
  donut: DashDonutSlice[];
  totalAnnual: number;
  currency: string | null;
}) {
  const taxed = rows.some((r) => r.tax !== null && r.tax !== undefined);
  if (!taxed) {
    return <Donut slices={donut} centerLabel="Total Income" centerValue={money(totalAnnual, currency)} currency={currency} />;
  }
  // Provided/reconciled net wins; else fall back to gross − calculated tax.
  const netOf = (r: DashIncomeRow) =>
    r.netProvided != null ? r.netProvided : r.annual - (r.tax ?? 0);
  const grossSlices = rows.map((r) => ({ label: r.label, amount: r.annual }));
  const netSlices = rows.map((r) => ({ label: r.label, amount: netOf(r) }));
  const netTotal = netSlices.reduce((sum, s) => sum + s.amount, 0);
  return (
    <div className="flex flex-col gap-[12px]" data-testid="fp-income-charts">
      <div className="flex flex-wrap gap-md justify-center">
        <div className="flex flex-col items-center flex-1 min-w-[240px]">
          <div className="text-body-03 font-medium text-black pb-sm">Before tax</div>
          <Donut slices={grossSlices} centerLabel="Gross" centerValue={money(totalAnnual, currency)} currency={currency} hideLegend />
        </div>
        <div className="flex flex-col items-center flex-1 min-w-[240px]">
          <div className="text-body-03 font-medium text-black pb-sm">After tax</div>
          <Donut slices={netSlices} centerLabel="Net" centerValue={money(netTotal, currency)} currency={currency} hideLegend />
        </div>
      </div>
      <DonutLegend slices={grossSlices} />
    </div>
  );
}

function DonutLegend({ slices }: { slices: DashDonutSlice[] }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-md gap-y-sm" data-testid="fp-donut-legend">
      {slices.map((s, i) => (
        <div key={s.label} className="flex items-center gap-sm">
          <span style={{ width: 12, height: 12, borderRadius: 4, background: PALETTE[i % PALETTE.length] }} />
          <span className="text-body-03 text-grey-04">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function RowsTable({
  rows,
  currency,
  negative,
}: {
  rows: Array<{ label: string; owner: string; amount: number }>;
  currency: string | null;
  negative?: boolean;
}) {
  return (
    <table className="w-full border-collapse text-body-03">
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={i === 0 ? '' : 'border-t border-[#E1E1E1]'}>
            <td className="py-[10px] pr-sm text-black">{r.label}</td>
            {r.owner && <td className="py-[10px] px-sm text-grey-04">{r.owner}</td>}
            <td className={`py-[10px] pl-sm text-right ${negative ? 'text-[#C0392B]' : 'text-[#1E8E4E]'}`}>
              {money(r.amount, currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Tax components shown in the per-income breakdown dialog, in display order. */
const TAX_COMPONENTS = ['Income tax', 'USC', 'PRSI', 'DIRT'] as const;

/**
 * A small provenance chip — Provided (client-entered), Calculated (engine), or
 * Reconciled (both, provided wins). Renders nothing when source is absent
 * (figure-provenance flag off).
 */
function ProvenanceChip({ source }: { source: DashIncomeRow['grossSource'] }) {
  if (!source) return null;
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    PROVIDED: { bg: '#EAF6EE', fg: '#1E8E4E', label: 'Provided' },
    CALCULATED: { bg: '#F0EEFF', fg: '#2D0A6C', label: 'Calculated' },
    RECONCILED: { bg: '#FDF1E3', fg: '#B9770E', label: 'Reconciled' },
  };
  const s = styles[source];
  if (!s) return null;
  return (
    <span
      data-testid={`fp-provenance-${source}`}
      className="ml-[6px] inline-block rounded-[6px] px-[6px] py-[1px] align-middle text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

/**
 * Income card table — gross per row, plus an apportioned Tax column + a
 * breakdown trigger when the tax layer is on. The dialog shows the year-0
 * Income tax / USC / PRSI / DIRT split for the selected income item.
 */
function IncomeTable({
  rows,
  currency,
  profileId,
  onReconciled,
}: {
  rows: DashIncomeRow[];
  currency: string | null;
  profileId: string;
  onReconciled: () => void;
}) {
  const [selected, setSelected] = useState<DashIncomeRow | null>(null);
  const [reconciling, setReconciling] = useState<DashIncomeRow | null>(null);
  const [timelineFor, setTimelineFor] = useState<DashIncomeRow | null>(null);
  const taxed = rows.some((r) => r.tax !== null && r.tax !== undefined);
  // The reconcile affordance is shown only when the figure-provenance layer is on
  // (signalled by a non-null grossSource on the rows).
  const reconcilable = rows.some((r) => r.grossSource != null);
  return (
    <>
      <table className="w-full border-collapse text-body-03">
        {taxed && (
          <thead>
            <tr className="text-left text-grey-04">
              <th className="py-sm pr-sm font-medium">Source</th>
              <th className="py-sm px-sm font-medium">Owner</th>
              <th className="py-sm px-sm text-right font-medium">Gross</th>
              <th className="py-sm px-sm text-right font-medium">Tax</th>
              <th className="py-sm pl-sm text-right font-medium">Breakdown</th>
              {reconcilable && <th className="py-sm pl-sm text-right font-medium">Reconcile</th>}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.itemId ?? i} className={!taxed && i === 0 ? '' : 'border-t border-[#E1E1E1]'}>
              <td className="py-[10px] pr-sm text-black">{r.label}</td>
              <td className="py-[10px] px-sm text-grey-04">{r.owner}</td>
              <td className="py-[10px] px-sm text-right text-[#1E8E4E]">
                {money(r.annual, currency)}
                <ProvenanceChip source={r.grossSource} />
              </td>
              {taxed && (
                <>
                  <td className="py-[10px] px-sm text-right text-[#C0392B]">
                    {r.tax ? `-${money(r.tax, currency)}` : '—'}
                    {r.netWithinTolerance === false && r.netDiscrepancy != null && (
                      <span
                        data-testid={`fp-discrepancy-${r.itemId}`}
                        className="ml-[6px] align-middle text-[11px] font-medium text-[#B9770E]"
                        title={`Your net differs from our estimate by ${money(Math.abs(r.netDiscrepancy), currency)}`}
                      >
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className="py-[10px] pl-sm text-right">
                    {r.tax ? (
                      <button
                        type="button"
                        data-testid={`fp-income-tax-${r.itemId}`}
                        aria-label={`Show tax breakdown for ${r.label}`}
                        title="Show tax breakdown"
                        onClick={() => setSelected(r)}
                        className="inline-flex items-center gap-[6px] rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-purple-primary hover:bg-lilac-06"
                      >
                        <AppIcon name="chart" size={16} color="#2D0A6C" />
                      </button>
                    ) : (
                      <span className="text-[#A3A3A3]">—</span>
                    )}
                  </td>
                </>
              )}
              {taxed && reconcilable && (
                <td className="py-[10px] pl-sm text-right">
                  {r.grossSource != null ? (
                    <span className="inline-flex items-center gap-[6px]">
                      <button
                        type="button"
                        data-testid={`fp-reconcile-${r.itemId}`}
                        aria-label={`Reconcile ${r.label}`}
                        title="Reconcile gross / net"
                        onClick={() => setReconciling(r)}
                        className="inline-flex items-center justify-center rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-purple-primary hover:bg-lilac-06"
                      >
                        <AppIcon name="edit" size={16} color="#2D0A6C" />
                      </button>
                      <button
                        type="button"
                        data-testid={`fp-timeline-${r.itemId}`}
                        aria-label={`Timeline for ${r.label}`}
                        title="Future-dated changes (timeline)"
                        onClick={() => setTimelineFor(r)}
                        className="inline-flex items-center justify-center rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-purple-primary hover:bg-lilac-06"
                      >
                        <AppIcon name="calendar" size={16} color="#2D0A6C" />
                      </button>
                    </span>
                  ) : (
                    <span className="text-[#A3A3A3]">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {selected && <TaxBreakdownModal row={selected} currency={currency} onClose={() => setSelected(null)} />}
      {reconciling && (
        <ReconcileIncomeModal
          row={reconciling}
          profileId={profileId}
          onClose={() => setReconciling(null)}
          onSaved={() => {
            setReconciling(null);
            onReconciled();
          }}
        />
      )}
      {timelineFor && (
        <FigureTimelineModal
          row={timelineFor}
          currency={currency}
          profileId={profileId}
          onClose={() => setTimelineFor(null)}
          onChanged={onReconciled}
        />
      )}
    </>
  );
}

/** Lists a figure's dated versions and lets the advisor add / remove future ones. */
function FigureTimelineModal({
  row,
  currency,
  profileId,
  onClose,
  onChanged,
}: {
  row: DashIncomeRow;
  currency: string | null;
  profileId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [versions, setVersions] = useState<FigureVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validFrom, setValidFrom] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    financialPositionApi
      .getIncomeTimeline(profileId, row.itemId)
      .then(setVersions)
      .catch(() => setError('Could not load the timeline.'));
  };
  useEffect(load, [profileId, row.itemId]);

  const add = () => {
    if (busy) return;
    if (validFrom.trim() === '' || amount.trim() === '' || Number.isNaN(Number(amount))) {
      setError('Enter a future date and an amount.');
      return;
    }
    setBusy(true);
    setError(null);
    financialPositionApi
      .addIncomeVersion(profileId, row.itemId, { validFrom, amount: Number(amount), frequency: null })
      .then(() => {
        setValidFrom('');
        setAmount('');
        load();
        onChanged();
      })
      .catch(() => setError('Could not add the version. Use a future date that isn’t already on the timeline.'))
      .finally(() => setBusy(false));
  };

  const remove = (versionId: string) => {
    setBusy(true);
    financialPositionApi
      .deleteIncomeVersion(profileId, versionId)
      .then(() => {
        load();
        onChanged();
      })
      .catch(() => setError('Could not delete the version.'))
      .finally(() => setBusy(false));
  };

  return (
    <Modal open onClose={onClose} size="medium">
      <Modal.Header icon="calendar" title={`Timeline — ${row.label}`} onClose={onClose} />
      <Modal.Content>
        <div data-testid="fp-timeline-modal" className="flex flex-col gap-[12px]">
          <p className="text-body-03 text-grey-04">
            Add future-dated changes (e.g. a promotion). Each version applies from its date until the next one.
          </p>
          {error && <Alert variant="error">{error}</Alert>}
          <table className="w-full border-collapse text-body-03">
            <thead>
              <tr className="text-left text-grey-04">
                <th className="py-[6px] pr-sm font-medium">From</th>
                <th className="py-[6px] px-sm text-right font-medium">Amount</th>
                <th className="py-[6px] pl-sm text-right font-medium" />
              </tr>
            </thead>
            <tbody>
              {(versions ?? []).map((v) => (
                <tr key={v.id} className="border-t border-[#E1E1E1]">
                  <td className="py-sm pr-sm text-black">
                    {v.validFrom}
                    {v.base && <span className="ml-[6px] text-[11px] text-[#8A8A8A]">(current)</span>}
                  </td>
                  <td className="py-sm px-sm text-right text-black">{money(v.amount, currency)}</td>
                  <td className="py-sm pl-sm text-right">
                    {!v.base && (
                      <button
                        type="button"
                        data-testid={`fp-timeline-delete-${v.id}`}
                        aria-label="Delete version"
                        onClick={() => remove(v.id)}
                        disabled={busy}
                        className="text-[#C0392B] hover:underline disabled:opacity-60"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-end gap-sm">
            <label className="text-[13px] text-black">
              From
              <input
                type="date"
                data-testid="fp-timeline-date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                className="mt-xs block rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px]"
              />
            </label>
            <label className="text-[13px] text-black">
              Amount
              <input
                type="number"
                data-testid="fp-timeline-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-xs block rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px]"
              />
            </label>
            <button
              type="button"
              data-testid="fp-timeline-add"
              onClick={add}
              disabled={busy}
              className="rounded-lg bg-purple-primary px-[12px] py-[6px] text-body-03 text-white disabled:opacity-60"
            >
              Add
            </button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}

/** Advisor dialog to set an income item's gross/net basis + stated net + reason. */
function ReconcileIncomeModal({
  row,
  profileId,
  onClose,
  onSaved,
}: {
  row: DashIncomeRow;
  profileId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [basis, setBasis] = useState<'GROSS' | 'NET' | 'GROSS_AND_NET'>(
    (row.netSource === 'PROVIDED' && 'NET') || (row.netSource === 'RECONCILED' && 'GROSS_AND_NET') || 'GROSS',
  );
  const [net, setNet] = useState(row.netProvided != null ? String(row.netProvided) : '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    const statedNet = basis === 'GROSS' ? null : Number(net);
    if (basis !== 'GROSS' && (net.trim() === '' || Number.isNaN(statedNet as number))) {
      setError('Enter the annual net amount.');
      setSaving(false);
      return;
    }
    financialPositionApi
      .reconcileIncome(profileId, row.itemId, { entryBasis: basis, statedNetAmount: statedNet, adjustmentReason: reason || null })
      .then(onSaved)
      .catch(() => setError('Could not save the reconciliation.'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal open onClose={onClose} size="medium">
      <Modal.Header icon="edit" title={`Reconcile — ${row.label}`} onClose={onClose} />
      <Modal.Content>
        <div data-testid="fp-reconcile-modal" className="flex flex-col gap-[12px]">
          <label className="text-body-03 text-black">
            What did the client provide?
            <select
              data-testid="fp-reconcile-basis"
              value={basis}
              onChange={(e) => setBasis(e.target.value as typeof basis)}
              className="mt-xs block w-full rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px]"
            >
              <option value="GROSS">Gross only</option>
              <option value="NET">Net only</option>
              <option value="GROSS_AND_NET">Both gross and net</option>
            </select>
          </label>
          {basis !== 'GROSS' && (
            <label className="text-body-03 text-black">
              Annual net amount
              <input
                type="number"
                data-testid="fp-reconcile-net"
                value={net}
                onChange={(e) => setNet(e.target.value)}
                className="mt-xs block w-full rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px]"
              />
            </label>
          )}
          {basis === 'GROSS_AND_NET' && (
            <label className="text-body-03 text-black">
              Reason for the difference (optional)
              <input
                type="text"
                data-testid="fp-reconcile-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. AVC, salary sacrifice"
                className="mt-xs block w-full rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px]"
              />
            </label>
          )}
          {error && <Alert variant="error">{error}</Alert>}
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="fp-reconcile-save"
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-purple-primary px-[12px] py-[6px] text-body-03 text-white disabled:opacity-60"
            >
              Save
            </button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}

/** Per-income tax breakdown dialog (year 0, today's money). */
function TaxBreakdownModal({
  row,
  currency,
  onClose,
}: {
  row: DashIncomeRow;
  currency: string | null;
  onClose: () => void;
}) {
  const comp = row.taxByComponent ?? {};
  const lines = TAX_COMPONENTS.filter((k) => (comp[k] ?? 0) > 0);
  const net = row.annual - (row.tax ?? 0);
  return (
    <Modal open onClose={onClose} size="medium">
      <Modal.Header icon="chart" title={`Tax breakdown — ${row.label}`} onClose={onClose} />
      <Modal.Content>
        <div data-testid="fp-income-tax-modal">
          <p className="text-body-03 text-grey-04 pb-[12px]">
            Estimated annual tax on this income (current year, today&apos;s money). Tax is apportioned across the
            owner&apos;s income, so figures are indicative rather than a per-source assessment.
          </p>
          <table className="w-full border-collapse text-body-03">
            <tbody>
              <tr>
                <td className="py-[10px] pr-sm text-black">Gross income</td>
                <td className="py-[10px] pl-sm text-right font-medium text-[#1E8E4E]">{money(row.annual, currency)}</td>
              </tr>
              {lines.length > 0 ? (
                lines.map((k) => (
                  <tr key={k} className="border-t border-[#E1E1E1]">
                    <td className="py-[10px] pr-sm text-black">{k}</td>
                    <td className="py-[10px] pl-sm text-right text-[#C0392B]">-{money(comp[k], currency)}</td>
                  </tr>
                ))
              ) : (
                <tr className="border-t border-[#E1E1E1]">
                  <td className="py-[10px] pr-sm text-grey-04" colSpan={2}>
                    No tax on this income (exempt).
                  </td>
                </tr>
              )}
              <tr className="border-t-2 border-[#E1E1E1] font-medium">
                <td className="py-[10px] pr-sm text-black">Total tax</td>
                <td className="py-[10px] pl-sm text-right text-[#C0392B]">
                  {row.tax ? `-${money(row.tax, currency)}` : money(0, currency)}
                </td>
              </tr>
              <tr className="border-t border-[#E1E1E1] font-medium">
                <td className="py-[10px] pr-sm text-black">Net income</td>
                <td className="py-[10px] pl-sm text-right text-[#1E8E4E]">{money(net, currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal.Content>
    </Modal>
  );
}

function PropertyTable({
  property,
  currency,
  profileId,
}: {
  property: Dashboard['property'];
  currency: string | null;
  profileId: string;
}) {
  const [selected, setSelected] = useState<DashPropertyRow | null>(null);
  return (
    <>
      <table className="w-full border-collapse text-body-03">
        <thead>
          <tr className="text-left text-grey-04">
            <th className="py-sm pr-sm font-medium">Property</th>
            <th className="py-sm px-sm font-medium">Owner</th>
            <th className="py-sm px-sm text-right font-medium">Value</th>
            <th className="py-sm px-sm text-right font-medium">Mortgage</th>
            <th className="py-sm px-sm text-right font-medium">Equity</th>
            <th className="py-sm pl-sm text-right font-medium">Future value</th>
          </tr>
        </thead>
        <tbody>
          {property.rows.map((p, i) => (
            <tr key={i} className="border-t border-[#E1E1E1]">
              <td className="py-[10px] pr-sm text-black">{p.label}</td>
              <td className="py-[10px] px-sm text-black">{p.owner}</td>
              <td className="py-[10px] px-sm text-right font-medium text-[#1E8E4E]">{money(p.value, currency)}</td>
              <td className="py-[10px] px-sm text-right font-medium text-[#C0392B]">
                {p.mortgage ? `-${money(p.mortgage, currency)}` : '—'}
              </td>
              <td className={`py-[10px] px-sm text-right font-medium ${p.equity < 0 ? 'text-[#C0392B]' : 'text-[#1E8E4E]'}`}>
                {money(p.equity, currency)}
              </td>
              <td className="py-[10px] pl-sm text-right">
                {p.projectionDirection ? (
                  <button
                    type="button"
                    data-testid={`fp-future-values-${p.assetId}`}
                    aria-label={`Show future value of ${p.label}`}
                    title="Show future value"
                    onClick={() => setSelected(p)}
                    className="inline-flex items-center gap-[6px] rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-purple-primary hover:bg-lilac-06"
                  >
                    <span aria-hidden className="inline-block w-[16px] text-center text-body-03 font-semibold">
                      {p.projectionDirection === 'UP' ? '↗' : '→'}
                    </span>
                    <AppIcon name="chart" size={16} color="#2D0A6C" />
                  </button>
                ) : (
                  <span className="text-[#A3A3A3]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[#E1E1E1] text-black font-medium">
            <td className="py-[10px] pr-sm" colSpan={2}>Total</td>
            <td className="py-[10px] px-sm text-right">{money(property.totalValue, currency)}</td>
            <td className="py-[10px] px-sm text-right text-[#C0392B]">
              {property.totalMortgage > 0 ? `-${money(property.totalMortgage, currency)}` : '—'}
            </td>
            <td className="py-[10px] px-sm text-right">{money(property.totalEquity, currency)}</td>
            <td className="py-[10px] pl-sm" />
          </tr>
        </tfoot>
      </table>
      {selected && (
        <ProjectionModal
          fetchKey={selected.assetId}
          title={selected.label}
          currency={currency}
          load={() => financialPositionApi.getPolicyProjection(profileId, 'assets', selected.assetId)}
          rateControl={{
            onSave: (r) => financialPositionApi.setPolicyGrowthRate(profileId, 'assets', selected.assetId, r),
          }}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function AccountBalancesTable({
  rows,
  currency,
  profileId,
}: {
  rows: Dashboard['accountBalances'];
  currency: string | null;
  profileId: string;
}) {
  const [selected, setSelected] = useState<DashAccountRow | null>(null);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [savingSweep, setSavingSweep] = useState(false);

  useEffect(() => {
    financialPositionApi.listAccounts(profileId).then(setAccounts).catch(() => setAccounts([]));
  }, [profileId]);

  // Positive (ASSET) accounts are the only eligible sweep targets. The effective
  // sweep account is the advisor-flagged one, else the first positive account.
  const positive = accounts.filter((a) => a.classification === 'ASSET' && (a.balance ?? 0) > 0);
  const flaggedId = accounts.find((a) => a.sweepTarget)?.id ?? null;
  const effectiveSweepId = flaggedId ?? positive[0]?.id ?? null;

  const chooseSweep = (accountId: string) => {
    if (savingSweep || accountId === effectiveSweepId) return;
    setSavingSweep(true);
    financialPositionApi
      .setSweepAccount(profileId, accountId)
      .then(() => financialPositionApi.listAccounts(profileId).then(setAccounts))
      .catch(() => {})
      .finally(() => setSavingSweep(false));
  };

  return (
    <>
      {positive.length > 0 && (
        <div
          data-testid="fp-sweep-control"
          className="flex items-center gap-sm text-body-03 text-grey-04"
        >
          <span>Surplus sweep account</span>
          <select
            data-testid="fp-sweep-select"
            aria-label="Surplus sweep account"
            disabled={savingSweep || positive.length < 2}
            value={effectiveSweepId ?? ''}
            onChange={(e) => chooseSweep(e.target.value)}
            className="rounded-lg border border-grey-05 bg-white px-sm py-xs text-black disabled:opacity-60"
          >
            {positive.map((a) => (
              <option key={a.id} value={a.id}>
                {a.institutionName || a.accountType}
                {a.balance != null ? ` — ${money(a.balance, currency)}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {positive.length === 0 && accounts.length >= 0 && (
        <p data-testid="fp-sweep-synthetic" className="text-body-04 text-grey-03 m-0">
          Surplus sweeps into a simulated “Surplus” account (no positive account to designate).
        </p>
      )}
      <table className="w-full border-collapse text-body-03">
        <thead>
          <tr className="text-left text-grey-04">
            <th className="py-sm pr-sm font-medium">Account</th>
            <th className="py-sm px-sm font-medium">Owner</th>
            <th className="py-sm px-sm text-right font-medium">Balance</th>
            <th className="py-sm pl-sm text-right font-medium">Future value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a, i) => (
            <tr key={i} className="border-t border-[#E1E1E1]">
              <td className="py-[10px] pr-sm text-black">
                {a.label}
                {a.accountId === effectiveSweepId && (
                  <span
                    data-testid={`fp-sweep-badge-${a.accountId}`}
                    title="Each year's surplus is swept into this account"
                    className="ml-sm align-middle rounded-full bg-lilac-05 px-sm py-xs text-body-04 font-medium text-purple-primary"
                  >
                    Sweep
                  </span>
                )}
              </td>
              <td className="py-[10px] px-sm text-black">{a.owner}</td>
              <td className={`py-[10px] px-sm text-right font-medium ${a.amount < 0 ? 'text-[#C0392B]' : 'text-[#1E8E4E]'}`}>
                {money(a.amount, currency)}
                {a.balanceEstimated && (
                  <span
                    className="ml-[6px] align-middle text-[11px] font-normal text-[#8A8A8A]"
                    title="Estimated from the repayment schedule — no outstanding balance was provided"
                    data-testid={`fp-estimated-${a.accountId}`}
                  >
                    (est.)
                  </span>
                )}
              </td>
              <td className="py-[10px] pl-sm text-right">
                {a.projectionDirection ? (
                  <button
                    type="button"
                    data-testid={`fp-future-values-${a.accountId}`}
                    aria-label={`Show future value of ${a.label}`}
                    title="Show future value"
                    onClick={() => setSelected(a)}
                    className="inline-flex items-center gap-[6px] rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-purple-primary hover:bg-lilac-06"
                  >
                    <span aria-hidden className="inline-block w-[16px] text-center text-body-03 font-semibold">
                      {a.projectionDirection === 'UP' ? '↗' : a.projectionDirection === 'DOWN' ? '↘' : '→'}
                    </span>
                    <AppIcon name="chart" size={16} color="#2D0A6C" />
                  </button>
                ) : (
                  <span className="text-[#A3A3A3]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {selected && (
        <ProjectionModal
          fetchKey={selected.accountId}
          title={selected.label}
          currency={currency}
          load={() => financialPositionApi.getAccountProjection(profileId, selected.accountId)}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

/**
 * Per-policy Future-Values triggers for the Pensions / Investments cards. Each
 * projectable policy opens the projection dialog with an in-dialog growth-rate input.
 */
function PolicyTriggers({
  policies,
  kind,
  currency,
  profileId,
}: {
  policies: DashPolicyValue[];
  /** Card group (for the test id); each row's own projectionKind drives the endpoint. */
  kind: 'pensions' | 'investments';
  currency: string | null;
  profileId: string;
}) {
  const [selected, setSelected] = useState<DashPolicyValue | null>(null);
  if (policies.length === 0) return null;
  return (
    <div className="flex flex-col gap-sm pt-md" data-testid={`fp-policy-list-${kind}`}>
      {policies.map((p) => (
        <div key={p.policyId} className="flex items-center justify-between text-body-03 border-t border-[#E1E1E1] pt-sm">
          <span className="text-black">
            {p.label} <span className="text-grey-04">· {p.owner}</span>
          </span>
          <div className="flex items-center gap-[10px]">
            <span className="font-medium text-black">{money(p.value, currency)}</span>
            {p.projectionDirection ? (
              <button
                type="button"
                data-testid={`fp-future-values-${p.policyId}`}
                aria-label={`Show future value of ${p.label}`}
                title="Show future value"
                onClick={() => setSelected(p)}
                className="inline-flex items-center gap-[6px] rounded-lg border-[1.5px] border-[#BDB8FF] bg-white px-[10px] py-[6px] text-purple-primary hover:bg-lilac-06"
              >
                <span aria-hidden className="inline-block w-[16px] text-center text-body-03 font-semibold">
                  {p.projectionDirection === 'UP' ? '↗' : '→'}
                </span>
                <AppIcon name="chart" size={16} color="#2D0A6C" />
              </button>
            ) : (
              <span className="text-[#A3A3A3]">—</span>
            )}
          </div>
        </div>
      ))}
      {selected && (
        <ProjectionModal
          fetchKey={selected.policyId}
          title={selected.label}
          currency={currency}
          load={() => financialPositionApi.getPolicyProjection(profileId, selected.projectionKind, selected.policyId)}
          rateControl={{
            onSave: (r) => financialPositionApi.setPolicyGrowthRate(profileId, selected.projectionKind, selected.policyId, r),
          }}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/** Line colours for a multi-series property projection. */
const SERIES_COLORS: Record<string, string> = {
  value: '#7C5CFC',
  mortgage: '#C0392B',
  equity: '#1E8E4E',
};

/**
 * LineChart rows: when a breakdown is present (property Value/Mortgage/Equity),
 * merge its series into one row per year; otherwise the single value series.
 */
function projectionChartData(projection: FinancialProjection): Array<Record<string, number>> {
  const b = projection.breakdown;
  if (b && b.length > 0) {
    return b[0].series.map((pt, i) => {
      const row: Record<string, number> = { year: pt.year };
      for (const s of b) row[s.key] = s.series[i]?.value ?? 0;
      return row;
    });
  }
  return projection.series.map((pt) => ({ year: pt.year, value: pt.value }));
}

/** Lazy-loads + plots a year-by-year value series, with an optional growth-rate input. */
function ProjectionModal({
  fetchKey,
  title,
  currency,
  load,
  rateControl,
  onClose,
}: {
  fetchKey: string;
  title: string;
  currency: string | null;
  load: () => Promise<FinancialProjection | null>;
  rateControl?: { onSave: (rate: number | null) => Promise<FinancialProjection | null> };
  onClose: () => void;
}) {
  const [projection, setProjection] = useState<FinancialProjection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .then((p) => {
        if (cancelled) return;
        if (!p) setError('This item is no longer projectable.');
        else setProjection(p);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the projection.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const applyRate = () => {
    if (!rateControl || saving) return;
    setSaving(true);
    setError(null);
    const parsed = rate.trim() === '' ? null : Number(rate);
    rateControl
      .onSave(Number.isNaN(parsed as number) ? null : parsed)
      .then((p) => {
        if (p) setProjection(p);
      })
      .catch(() => setError('Could not update the growth rate.'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header icon="chart" title={`Future value — ${title}`} onClose={onClose} />
      <Modal.Content>
        <div data-testid="fp-projection-modal">
          {rateControl && (
            <div className="flex items-center gap-sm pb-[12px]">
              <label htmlFor="fp-rate-input" className="text-body-03 text-black">Annual growth rate</label>
              <input
                id="fp-rate-input"
                type="number"
                data-testid="fp-rate-input"
                value={rate}
                placeholder="%"
                onChange={(e) => setRate(e.target.value)}
                className="w-[88px] rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px] text-body-03"
              />
              <span className="text-body-03 text-grey-04">%</span>
              <button
                type="button"
                data-testid="fp-rate-save"
                onClick={applyRate}
                disabled={saving}
                className="rounded-lg bg-purple-primary px-[12px] py-[6px] text-body-03 text-white disabled:opacity-60"
              >
                Apply
              </button>
            </div>
          )}
          {loading && <Spinner />}
          {error && <Alert variant="error">{error}</Alert>}
          {projection && (
            <>
              <div className="text-body-03 text-grey-04 pb-[12px]">{projection.basis}</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={projectionChartData(projection)}
                  margin={{ left: 24, right: 16, top: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `Y${v}`}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    width={72}
                    tickFormatter={(v) => money(Number(v), projection.currency ?? currency)}
                  />
                  <Tooltip
                    formatter={(value) => money(Number(value), projection.currency ?? currency)}
                    labelFormatter={(label) => `Year ${label}`}
                  />
                  {projection.breakdown && projection.breakdown.length > 0 ? (
                    <>
                      <Legend />
                      {projection.breakdown.map((b) => (
                        <Line
                          key={b.key}
                          type="monotone"
                          dataKey={b.key}
                          name={b.label}
                          stroke={SERIES_COLORS[b.key] ?? '#7C5CFC'}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </>
                  ) : (
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={
                        projection.direction === 'UP'
                          ? '#1E8E4E'
                          : projection.direction === 'DOWN'
                            ? '#C0392B'
                            : '#7C5CFC'
                      }
                      strokeWidth={2}
                      dot={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}
